import { useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { FileModel } from "@/models/FileModel.ts";
import { RenewType } from "@/models/PatientRenewalInfoModel.ts";
import { patientFormattedName, userFormattedName } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { ColoredCard } from "@/uiKit/atoms/ColoredCard.tsx";
import { ProfilePicture } from "@/uiKit/atoms/ProfilePicture.tsx";
import { PatientRenewal } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";

import { Button } from "../../atoms/Button";

type DoctorRenewNudgeProps = {
  patientId: string;
};

type DoctorRenewNudgeBannerProps = {
  patientId: string;
  expiresIn: number;
  renewalType: RenewType;
  nurseFirstName?: string;
  nurseLastName?: string;
  nurseProfilePicture?: FileModel;
};

const DoctorRenewNudgeBannerExpired = ({
  patientId,
  expiresIn,
  renewalType,
  nurseFirstName,
  nurseLastName,
  nurseProfilePicture,
}: DoctorRenewNudgeBannerProps) => {
  const { t } = useTranslation();
  const refuseToRenew = Queries.practitioner.useRefuseToRenew();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const patient = Queries.practitioner.usePatient(patientId);

  const wordingContext = expiresIn == 1 ? "yesterday" : "";

  return (
    <ColoredCard id="renew-nudge" square border color="warning">
      <CardHeader
        startIcon={
          nurseFirstName &&
          nurseLastName && (
            <ProfilePicture
              id="renew-nudge-nurse-avatar"
              src={nurseProfilePicture?.url}
              firstName={nurseFirstName}
              lastName={nurseLastName}
              size="medium"
              tooltip={userFormattedName(nurseLastName, nurseFirstName)}
            />
          )
        }
        variant="banner"
        title={t("patientNavigation.renewalNudge.expired", {
          days: expiresIn,
          patientName: patientFormattedName(patient.familyName, patient.givenName),
          context: wordingContext,
        })}
      />
      <CardFooter position="start">
        <Button
          variant="contained"
          data-action="care-plan.renewal.navigate"
          onClick={() => setIsModalOpen(true)}
          id="renew-from-renew-nudge"
        >
          {t("patientNavigation.renewalNudge.ctas.continue")}
        </Button>
        <Button
          onClick={() => refuseToRenew.mutate(patientId)}
          variant="contained"
          data-action="care-plan.renewal.dismiss"
          pending={refuseToRenew.isPending}
          id="refused-to-renew"
        >
          {t("patientNavigation.renewalNudge.ctas.stop", {
            days: expiresIn,
            context: "now",
          })}
        </Button>
      </CardFooter>
      <PatientRenewal
        renewalType={renewalType}
        patientId={patientId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={() => setIsModalOpen(false)}
      />
    </ColoredCard>
  );
};

const DoctorRenewNudgeBannerExpiresSoon = ({
  patientId,
  expiresIn,
  renewalType,
  nurseFirstName,
  nurseLastName,
  nurseProfilePicture,
}: DoctorRenewNudgeBannerProps) => {
  const { t } = useTranslation();
  const refuseToRenew = Queries.practitioner.useRefuseToRenew();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const patient = Queries.practitioner.usePatient(patientId);

  const ctaContext = expiresIn === 1 ? "tomorrow" : expiresIn === 0 ? "today" : "later";
  const wordingContext = expiresIn == 0 ? "today" : expiresIn == 1 ? "tomorrow" : "soon";

  return (
    <ColoredCard id="renew-nudge" border square color="purple">
      <CardHeader
        startIcon={
          nurseFirstName &&
          nurseLastName && (
            <ProfilePicture
              id="renew-nudge-nurse-avatar"
              src={nurseProfilePicture?.url}
              firstName={nurseFirstName}
              lastName={nurseLastName}
              size="medium"
              tooltip={userFormattedName(nurseLastName, nurseFirstName)}
            />
          )
        }
        variant="banner"
        title={t("patientNavigation.renewalNudge.expires", {
          days: expiresIn,
          patientName: patientFormattedName(patient.familyName, patient.givenName),
          context: wordingContext,
        })}
      />
      <CardFooter position="start">
        <Button
          variant="contained"
          data-action="care-plan.renewal.navigate"
          onClick={() => setIsModalOpen(true)}
          id="renew-from-renew-nudge"
        >
          {t("patientNavigation.renewalNudge.ctas.continue")}
        </Button>
        <Button
          onClick={() => refuseToRenew.mutate(patientId)}
          variant="contained"
          data-action="care-plan.renewal.dismiss"
        >
          {t("patientNavigation.renewalNudge.ctas.stop", {
            days: expiresIn,
            context: ctaContext,
          })}
        </Button>
      </CardFooter>
      <PatientRenewal
        renewalType={renewalType}
        patientId={patientId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={() => setIsModalOpen(false)}
      />
    </ColoredCard>
  );
};

export const DoctorRenewNudge = ({ patientId }: DoctorRenewNudgeProps) => {
  const nudge = Queries.practitioner.useShouldNudgeRenewal(patientId);
  const latestTLSNurse = Queries.practitioner.useCarePlanHistory(patientId, {
    select: carePlan => (carePlan.length > 0 ? carePlan[0].nurse : undefined),
  });

  if (nudge.shouldNudge && nudge.lastCpEnd) {
    const lastCarePlanExpire = toDateTime(nudge.lastCpEnd);
    const now = DateTime.now().startOf("day");

    const diff = lastCarePlanExpire.diff(now, ["days"]);
    const numberOfDays = Math.round(diff.days);

    if (numberOfDays < 0) {
      return (
        <DoctorRenewNudgeBannerExpired
          patientId={patientId}
          expiresIn={-1 * numberOfDays}
          renewalType={nudge.renewalType}
          nurseFirstName={latestTLSNurse?.firstName}
          nurseLastName={latestTLSNurse?.lastName}
          nurseProfilePicture={latestTLSNurse?.profilePicture}
        />
      );
    } else {
      return (
        <DoctorRenewNudgeBannerExpiresSoon
          expiresIn={numberOfDays}
          patientId={patientId}
          renewalType={nudge.renewalType}
          nurseFirstName={latestTLSNurse?.firstName}
          nurseLastName={latestTLSNurse?.lastName}
          nurseProfilePicture={latestTLSNurse?.profilePicture}
        />
      );
    }
  }

  return null;
};
